import { runWithLockingForm } from '../../global/loading-indicator';
import { html } from '../../global/template-literals';
import { TopModalCSSClass } from '../../global/constants';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import { UITemplate } from '../ui-template';
import type { UIModal } from '../modal/ui-modal';
import type { UIModalController } from '../modal/ui-modal-controller';
import type { UIModalActionType } from '../modal/ui-modal.types';
import type {
    ConfirmationDialogCallbacks,
    ConfirmationDialogInit,
} from './confirmation-dialog.template.type';

/**
 * @class
 * @deprecated
 * @memberof SharedComponents
 * @classdesc A confirmation dialog template to create a confirmation modal with a consistent design.
 * @alias ConfirmationDialogTemplate
 * @augments {UITemplate}
 * @property {ConfirmationDialogInit} _data Template data
 */
export class ConfirmationDialogTemplate extends UITemplate {
    protected _data: ConfirmationDialogInit;
    protected _trusted: UndefinedOr<boolean>;

    /**
     * @param {ConfirmationDialogInit} data
     * @param {boolean} [trusted]
     * @returns {ConfirmationDialogTemplate}
     */
    static create(
        data: ConfirmationDialogInit,
        trusted?: boolean
    ): ConfirmationDialogTemplate {
        return new ConfirmationDialogTemplate(data, trusted);
    }

    /**
     * @param {ConfirmationDialogInit} data
     * @param {boolean} [trusted]
     */
    constructor(data: ConfirmationDialogInit, trusted?: boolean) {
        super(data);
        this._data = Object.assign({ dynamic: true, compact: true }, data);
        this._trusted = trusted;
    }

    /**
     * @returns {boolean}
     */
    private isSingleModal(): boolean {
        const modalController: NullOr<UIModalController> =
            document.querySelector<UIModalController>('ui-modal-controller');
        return modalController?.getActiveFullViewModals().length === 0;
    }

    /**
     * @param {{onAccept: Function, onDecline: Function}} [callbacks]
     * @returns {UIModal}
     */
    prompt({ onAccept, onDecline }: ConfirmationDialogCallbacks = {}): UIModal {
        const modal: NullOr<UIModal> =
            this.render().querySelector<UIModal>('ui-modal');

        if (!modal) {
            throw new Error('Could not create ui-modal element');
        }

        const complete = (): void => {
            modal.closable = true;
            modal.close();
        };

        const handleAsyncAction = (
            request: Promise<void>,
            buttonSelector: string
        ): void => {
            const button =
                modal.querySelector<HTMLButtonElement>(buttonSelector);
            if (button) {
                void runWithLockingForm(
                    request,
                    button,
                    modal.modalWindow
                ).then(() => complete());
                return;
            }
            void request.then(() => complete());
        };

        modal.addEventListener('modal-action', (event: Event): void => {
            const detail = (event as CustomEvent<{ type: UIModalActionType }>)
                .detail;
            modal.closable = false;

            if (detail.type === 'accept') {
                if (typeof onAccept === 'function') {
                    const acceptRes = onAccept();
                    if (acceptRes instanceof Promise) {
                        handleAsyncAction(acceptRes, 'button.-accept');
                    } else {
                        complete();
                    }
                } else {
                    complete();
                }
            } else {
                if (typeof onDecline === 'function') {
                    const declineRes = onDecline();
                    if (declineRes instanceof Promise) {
                        handleAsyncAction(declineRes, 'button.-decline');
                    } else {
                        complete();
                    }
                } else {
                    complete();
                }
            }
        });

        return modal.open();
    }

    /**
     * @inheritDoc
     */
    render(): DocumentFragment {
        const isSingle = this.isSingleModal();
        return html`
            <ui-modal
                type="confirmation"
                dynamic="${this._data.dynamic ? '' : null}"
                compact="${this._data.compact ? '' : null}"
                label-accept="${this._data.labelAccept}"
                label-decline="${this._data.labelDecline}"
                class="${isSingle ? TopModalCSSClass : ''}"
            >
                ${this._data.content ||
                html`
                    <h2>${this._data.title}</h2>
                    ${this._data.description &&
                    html`<p>${this._data.description}</p>`}
                `}
            </ui-modal>
        `;
    }
}
